import {mapObject, groupBy, noop} from '@benchling/js-utils';
import {produce} from 'immer';
import reactUpdate from 'immutability-helper';
import $ from 'jquery';
import moment, {Moment} from 'moment';
import _ from 'underscore';

import EntryActions from 'actions/EntryActions';
import {assert} from 'Asserts';
import * as benchlingUtil from 'BenchlingUtil';
import {
  CalendarEntriesByDate,
  CalendarEntry,
  CalendarFile,
  CalendarFilesByDate,
  CalendarInteraction,
  CalendarItem,
  CalendarItemsByDate,
  CalendarPreferences,
  CalendarProcessTasksByDate,
  CalendarProject,
  CalendarProjectsByDate,
  CalendarRequest,
  CalendarRequestsByDate,
  CalendarView,
  getCalendarItemProject,
  getCalendarItemProjectId,
  isCalendarProcessTask,
  isCalendarRequest,
  LayeredCalendarItem,
  LayeredCalendarItems,
  ProjectColorMapping,
  ViewRange,
} from 'calendar/CalendarConstants';
import {EventConstants} from 'constants/EventConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as SourceConstants from 'constants/SourceConstants';
import * as UserDAO from 'dao/UserDAO';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import notifications from 'notifications';
import {Party} from 'parties/PartyConstants';
import preferences from 'Preferences';
import APIUtil from 'util/APIUtil';
import {Optional} from 'util/Types';

import {SourceSummaryGraphQL} from '../../graphql-fragments/nav/sources/__generated/SourceSummaryGraphQL';
import {ProcessTaskForCalendarGraphQL} from '../../graphql-fragments/processes/__generated/ProcessTaskForCalendarGraphQL';
import CalendarUtil, {
  isDraggingInteraction,
  isInactiveInteraction,
  isSelectedItemInteraction,
} from './CalendarUtil';
import {scheduleLayers} from './LayerScheduler';

const {PROJECT_TYPE} = SourceConstants;

interface EntryRequestsMap {
  [entryId: string]: {
    xhr: JQueryPromise<unknown> | null;
    count: number;
  };
}

interface EntryTaskUpdateAction {
  date: Moment;
  entryId: string;
  notePartProvenanceId: number;
  completed: boolean;
}

function checkMomentType(
  isRequired: boolean,
  props: {[key: string]: unknown},
  propName: string,
  componentName: string
): Error | null {
  if (!isRequired && props[propName] == null) {
    return null;
  }
  if (!moment.isMoment(props[propName])) {
    return new Error(`Expected ${propName} to be a moment object`);
  }
  return null;
}

const momentPT = checkMomentType.bind(null, false);
momentPT.isRequired = checkMomentType.bind(null, true);
export const momentPropType = momentPT;

// Returns flattened array of items with days on [startDate, endDate).
export function itemsInRange<T extends CalendarItem>(
  items: CalendarItemsByDate<T>,
  viewRange: ViewRange
): Array<LayeredCalendarItem<T>> {
  const [startDate, endDate] = viewRange;
  const filteredItems = [];
  const currentDate = startDate.clone();
  while (currentDate.isBefore(endDate)) {
    const dayItems = items[currentDate.format('YYYY-MM-DD')];
    if (dayItems != null) {
      filteredItems.push(...dayItems);
    }
    currentDate.add(1, 'days');
  }
  const mergedItems: Array<LayeredCalendarItem<T>> = [];
  const groupedItems = groupBy(filteredItems, ({id}) => id);
  Object.values(groupedItems).forEach((itemsById) => {
    // For each group of items with same ID, merge consecutive ones and push first one onto mergedItems.
    let lastItem: LayeredCalendarItem<T> | null = null;
    for (const item of _.sortBy(itemsById, 'date')) {
      if (
        lastItem != null &&
        CalendarUtil.getItemDate(lastItem)
          .clone()
          .add(CalendarUtil.getItemNumDays(lastItem), 'days')
          .isSame(CalendarUtil.getItemDate(item))
      ) {
        lastItem.numDays++;
      } else {
        // @ts-expect-error TypeScript + reactUpdate gets confused by adding the field here
        lastItem = reactUpdate(item, {numDays: {$set: 1}}) as LayeredCalendarItem<T>;
        mergedItems.push(lastItem);
      }
    }
  });
  return _.sortBy(mergedItems, 'date');
}

// Maps items (date => array of items) to same object with only selected projectIds
function filterItemsByProject<
  T extends CalendarFile | CalendarRequest | CalendarEntry | ProcessTaskForCalendarGraphQL,
>(
  items: CalendarItemsByDate<T>,
  hiddenProjectIds: Array<string>,
  includeEntries?: boolean
): CalendarItemsByDate<T> {
  if (includeEntries == null) {
    includeEntries = true;
  }
  const hiddenProjectIdsHash: {[key: string]: boolean} = _.object(hiddenProjectIds.map((id) => [id, true]));
  return _.mapObject(items, (items) =>
    items.filter((item) => {
      // requests have a projectId prop whereas entries and files have {source: {id}}
      const projectId = getCalendarItemProjectId(item);
      const isProjectHidden = projectId != null && hiddenProjectIdsHash[projectId];
      return (
        (includeEntries || APIUtil.apiIdToType(item.id) !== ModelTypeConstants.ENTRY) && !isProjectHidden
      );
    })
  );
}

class CalendarStore extends Store {
  entries!: CalendarEntriesByDate;
  files!: CalendarFilesByDate;
  requests!: CalendarRequestsByDate;
  processTasks!: CalendarProcessTasksByDate;

  currentView!: CalendarView;
  visibleDate!: Moment;
  today!: Moment;
  interact!: CalendarInteraction;

  hiddenProjectIds!: Array<string>;
  hiddenMemberIds!: Array<string>;

  _outboundRequests!: number;
  _entryRequests!: EntryRequestsMap;
  _intervalId!: NodeJS.Timeout | null;

  _resetTodayInterval(): void {
    // Set up interval to keep "today" in sync.
    const CalendarActions = require('./CalendarActions').default;
    if (this._intervalId != null) {
      clearInterval(this._intervalId);
    }
    this._intervalId = setInterval(CalendarActions.updateToday, 1000 * 60);
  }

  resetState(): void {
    this._resetTodayInterval();
    this.currentView = preferences.get(CalendarPreferences.PREFERRED_CALENDAR_VIEW) || CalendarView.DAY_VIEW;
    // Which date must be visible - depending on month, week, or day other days are shown as well.
    this.visibleDate = moment();
    this.hiddenProjectIds = [];
    this.hiddenMemberIds = [];
    this.entries = {};
    this.files = {};
    this.requests = {};
    this.processTasks = {};
    this.today = this._makeToday();
    // Keep track of calendar item interaction related properties
    // {id, date} corresponding to selected item and date.
    this.interact = {};
    this._outboundRequests = 0;
    // Maps entryId -> {count, xhr} to keep track of all open requests. This allows us to chain XHR's to
    // handle serializing updates to the entry.
    this._entryRequests = {};
  }

  _makeToday(): Moment {
    return moment().startOf('day');
  }

  _periodRange(): ViewRange {
    const periodStart = this.visibleDate.clone();
    const periodEnd = this.visibleDate.clone();
    switch (this.currentView) {
      case CalendarView.MONTH_VIEW:
        periodStart.startOf('month');
        periodEnd.startOf('month').add(1, 'months');
        break;
      case CalendarView.WEEK_VIEW:
        periodStart.startOf('week');
        periodEnd.startOf('week').add(2, 'weeks');
        break;
      case CalendarView.DAY_VIEW:
        periodStart.startOf('day');
        periodEnd.startOf('day').add(1, 'days');
        break;
      default:
        throw Error('Unexpected CalendarView');
    }
    return [periodStart, periodEnd];
  }

  getCurrentView(): CalendarView {
    return this.currentView;
  }

  getViewLabel(): string {
    const [periodStart, periodEnd] = this._periodRange();
    // Inclusive end for user-facing text.
    const periodEndInclusive = periodEnd.clone().subtract(1, 'days');
    switch (this.currentView) {
      case CalendarView.MONTH_VIEW:
        return periodStart.format('MMMM YYYY');
      case CalendarView.WEEK_VIEW:
        return `${periodStart.format('MMM D, YYYY')} - ${periodEndInclusive.format('MMM D, YYYY')}`;
      case CalendarView.DAY_VIEW:
        return periodStart.format('MMM D, YYYY');
      default:
        throw Error('Unexpected CalendarView');
    }
  }

  getViewRange(): ViewRange {
    const [viewStart, viewEnd] = this._periodRange();
    // Round for viewing full weeks.
    if (this.currentView === CalendarView.MONTH_VIEW) {
      viewStart.startOf('week');
      viewEnd.startOf('week').add(1, 'weeks');
    }
    return [viewStart, viewEnd];
  }

  getVisibleDate(): Moment {
    return this.visibleDate;
  }

  getHiddenProjectIds(): Array<string> {
    return this.hiddenProjectIds;
  }

  getHiddenMemberIds(): Array<string> {
    return this.hiddenMemberIds;
  }

  getAllProjects(): Array<CalendarProject> {
    const projects = [];
    const existingProjectIds: {[projectId: string]: boolean} = {};
    for (const items of [this.entries, this.files, this.requests, this.processTasks]) {
      for (const itemsByDate of Object.values(items)) {
        for (const item of itemsByDate) {
          let project = null;
          if (isCalendarProcessTask(item)) {
            project = item.process.folder.source;
          } else {
            project = item.source;
          }
          if (project == null || existingProjectIds[project.id] || project.sourceType !== PROJECT_TYPE) {
            continue;
          }
          projects.push(project);
          existingProjectIds[project.id] = true;
        }
      }
    }
    return projects;
  }

  getProjectColorMapping(): ProjectColorMapping {
    return CalendarUtil.getProjectColorMapping(this.getAllProjects());
  }

  getToday(): Moment {
    return this.today;
  }

  getInteract(): CalendarInteraction {
    return this.interact;
  }

  isLoadingData(): boolean {
    return this._outboundRequests > 0;
  }

  getLayeredItems(): LayeredCalendarItems<CalendarEntry | CalendarRequest | ProcessTaskForCalendarGraphQL> {
    const itemsToLayer: CalendarItemsByDate<CalendarEntry | CalendarRequest | ProcessTaskForCalendarGraphQL> =
      {
        ...this.getEntries(),
      };
    Object.entries(this.getRequests()).forEach(([date, requests]) => {
      if (itemsToLayer[date] == null) {
        itemsToLayer[date] = [];
      }
      itemsToLayer[date].push(...requests);
    });
    Object.entries(this.getProcessTasks()).forEach(([date, processTasks]) => {
      if (itemsToLayer[date] == null) {
        itemsToLayer[date] = [];
      }
      itemsToLayer[date].push(...processTasks);
    });
    return this.layerItems(itemsToLayer);
  }

  getEntries(): CalendarEntriesByDate {
    return filterItemsByProject(this.entries, this.hiddenProjectIds);
  }

  getProcessTasks(): CalendarProcessTasksByDate {
    return filterItemsByProject(this.processTasks, this.hiddenProjectIds);
  }

  getRequests(): CalendarRequestsByDate {
    return filterItemsByProject(this.requests, this.hiddenProjectIds);
  }

  getFiles(): CalendarFilesByDate {
    return filterItemsByProject(this.files, this.hiddenProjectIds);
  }

  getNonEntryFiles(): CalendarFilesByDate {
    return filterItemsByProject(this.files, this.hiddenProjectIds, false);
  }

  /**
   * Groups entries and files by date and returns a map from
   * (date string) -> projects that have entries or files on that date
   */
  getProjects(): CalendarProjectsByDate {
    const projects: CalendarProjectsByDate = {};

    const entries = this.getEntries();
    const files = this.getNonEntryFiles();
    const requests = this.getRequests();
    const processTasks = this.getProcessTasks();
    // Get all dates that have an active entry, file, or request
    const dates = _.union(Object.keys(entries), Object.keys(files), Object.keys(requests));

    for (const date of dates) {
      const items = _.union<CalendarEntry | CalendarFile | CalendarRequest | ProcessTaskForCalendarGraphQL>(
        entries[date],
        files[date],
        requests[date],
        processTasks[date]
      );
      for (const item of items) {
        const source = getCalendarItemProject(item);
        if (source?.sourceType !== PROJECT_TYPE || _.findWhere(projects[date], {id: source?.id}) != null) {
          // Project already accounted for
          continue;
        }

        // Only show projects for which there is a non-hidden collaborator
        const sourceCollaborators =
          isCalendarRequest(item) || isCalendarProcessTask(item) ? [] : item.source?.collaborators;
        const collaborators = _.union(
          [
            source && source.hasOwnProperty('__typename')
              ? ((source as SourceSummaryGraphQL).ownerJson as Party)
              : ((source as Optional<SourceConstants.SourceSummary>)?.s_owner as Party),
          ],
          sourceCollaborators ?? []
        );
        const userCollaborators = collaborators.filter(({type}) => type === ModelTypeConstants.USER);
        const collaboratorIds = userCollaborators.map(({id}) => id);
        if (
          this.hiddenMemberIds.length > 0 &&
          _.difference(collaboratorIds, this.hiddenMemberIds).length === 0
        ) {
          continue;
        }

        // @ts-expect-error
        const project = reactUpdate<CalendarProject>(source, {
          $merge: {collaborators, date: CalendarUtil.getItemDate(item)},
        });

        if (projects[date]) {
          projects[date].push(project);
        } else {
          projects[date] = [project];
        }
      }
    }
    return projects;
  }

  getLayeredProjects(): LayeredCalendarItems<CalendarProject> {
    return this.layerItems(this.getProjects());
  }

  layerItems<T extends CalendarItem>(items: CalendarItemsByDate<T>): LayeredCalendarItems<T> {
    // Based on view range, returns layered calendar items - array of layers,
    // with each layer containing 0 or more non-overlapping items.
    // Items on consecutive days are merged together - each item labeled with numDays.
    const filteredItems = itemsInRange(items, this.getViewRange());
    const sortedItems = CalendarUtil.sortCalendarItems(filteredItems);
    return scheduleLayers(sortedItems);
  }

  _updateCalendarEntryTask(
    date: Moment,
    entryId: string,
    notePartProvenanceId: number,
    completed: boolean
  ): void {
    const dateKey = date.format('YYYY-MM-DD');
    const entryIdx = this.entries[dateKey].findIndex((entry) => entry.id === entryId);
    assert(entryIdx > -1, `Could not find entry ${entryId} on ${dateKey} in store.`);
    const entry = this.entries[dateKey][entryIdx];
    const entryTaskIdx = entry.entryTasks.findIndex(
      (entryTask) => entryTask.notePartProvenanceId === notePartProvenanceId
    );
    assert(entryTaskIdx > -1, `Could not find entry task ${entryIdx} in store.`);

    // Update the entry task in entryTasks to reflect {completed}.
    const entryTasks = reactUpdate(entry.entryTasks, {[entryTaskIdx]: {$merge: {completed}}});
    // Merge the new entryTasks into the calendar entries state.
    const newEntries = reactUpdate(this.entries[dateKey], {[entryIdx]: {$merge: {entryTasks}}});
    this.entries = reactUpdate(this.entries, {[dateKey]: {$set: newEntries}});
  }

  _queueActionForEntry(
    entryId: string,
    action: () => JQueryPromise<unknown>,
    doneCallback: () => void,
    failCallback: (error: unknown) => void,
    emptyQueueCallback: () => void
  ): void {
    // Queues an @{action} that updates entry with @{entryId}. This guarantees that the actions are
    // serialized and executed in the order in which they are queued. If there are many requests queued,
    // and a middle request fails, all remaining requests will also fail.
    // @{doneCallback}: A function that is called after the action succeeds.
    // @{failCallback}: A function that is called if the action fails.
    // @{emptyQueueCallback}: A function that is called when ALL open requests finish. If requests are queued
    //                        while other open requests are still executing, the emptyQueueCallback will not
    //                        run until all new requests have completed.

    // Request objects are queued by entryId since updates to different entries are independent of each other.
    let entryRequest = this._entryRequests[entryId];
    if (!entryRequest) {
      entryRequest = {xhr: null, count: 0};
      this._entryRequests[entryId] = entryRequest;
    }
    entryRequest.count++;

    function handleError(error: unknown): JQueryPromise<unknown> {
      if (typeof failCallback === 'function') {
        failCallback(error);
      }
      // Propagate the failure to the rest of the actions.
      return $.Deferred().reject().promise();
    }

    const newAction = (): JQueryPromise<unknown> =>
      benchlingUtil
        .requestFinishedOrTimedOut(action())
        .done(doneCallback)
        .fail((error) => {
          notifications.notifyError(error);
          handleError(error);
        });

    // Chain XHRs to run one after another.
    entryRequest.xhr = $.when(entryRequest.xhr)
      .then(newAction, handleError)
      .always(() => {
        entryRequest.count--;
        if (entryRequest.count === 0) {
          if (typeof emptyQueueCallback === 'function') {
            emptyQueueCallback();
          }
        }
      });
  }

  _updateEntryTask({date, entryId, notePartProvenanceId, completed}: EntryTaskUpdateAction): void {
    // Optimistically update the entry task to be {@completed}.
    this._updateCalendarEntryTask(date, entryId, notePartProvenanceId, completed);

    const done = noop;

    const action = (): JQueryPromise<unknown> =>
      UserDAO.updateCalendarEntryTask({entryId, notePartProvenanceId, completed});

    const failCallback = (): void => {
      // Revert the optimistic update to the entry task
      this._updateCalendarEntryTask(date, entryId, notePartProvenanceId, !completed);
    };

    function emptyQueueCallback(): void {
      EntryActions.open(entryId);
    }

    this._queueActionForEntry(entryId, action, done, failCallback, emptyQueueCallback);
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.CALENDAR_ENTRIES_FETCH:
      case EventConstants.CALENDAR_FILES_FETCH:
      case EventConstants.CALENDAR_REQUESTS_FETCH:
      case EventConstants.CALENDAR_PROCESS_TASKS_FETCH:
        this._outboundRequests++;
        this.emitChange();
        break;

      case EventConstants.CALENDAR_ENTRIES_FETCH_COMPLETED: {
        // Replace date with moment(date)
        const entries = mapObject(action.entries, (entriesOnDate: Array<CalendarEntry>) =>
          entriesOnDate.map((entry) => reactUpdate(entry, {date: {$set: moment(entry.date)}}))
        );
        this.entries = reactUpdate(this.entries, {$merge: entries});
        this._outboundRequests--;
        this.emitChange();
        break;
      }
      case EventConstants.CALENDAR_PROCESS_TASKS_FETCH_COMPLETED: {
        this.processTasks = produce(action.processTasks as CalendarProcessTasksByDate, (draftTasks) => ({
          ...this.processTasks,
          ...draftTasks,
        }));
        this._outboundRequests--;
        this.emitChange();
        break;
      }

      case EventConstants.CALENDAR_REQUESTS_FETCH_COMPLETED: {
        // Replace date with moment(date)
        const requests = produce(action.requests, (draftRequestsByDate: CalendarRequestsByDate) => {
          for (const date of Object.keys(draftRequestsByDate)) {
            draftRequestsByDate[date] = draftRequestsByDate[date].map((request) => ({
              ...request,
              scheduledOn: moment(request.scheduledOn),
            }));
          }
          return draftRequestsByDate;
        });
        this.requests = produce(this.requests, (draftRequests) => ({...draftRequests, ...requests}));
        this._outboundRequests--;
        this.emitChange();
        break;
      }

      case EventConstants.CALENDAR_ENTRIES_FETCH_FAILED:
      case EventConstants.CALENDAR_FILES_FETCH_FAILED:
      case EventConstants.CALENDAR_REQUESTS_FETCH_FAILED:
      case EventConstants.CALENDAR_PROCESS_TASKS_FETCH_FAILED:
        this._outboundRequests--;
        this.emitChange();
        break;

      case EventConstants.CALENDAR_FILES_FETCH_COMPLETED:
        this.files = reactUpdate(this.files, {$merge: action.files});
        this._outboundRequests--;
        this.emitChange();
        break;

      case EventConstants.CALENDAR_UPDATE_ENTRY_TASK:
        this._updateEntryTask(action as unknown as EntryTaskUpdateAction);
        this.emitChange();
        break;

      case EventConstants.CALENDAR_ITEM_DESELECTED:
        this.interact = reactUpdate(this.interact, {$unset: ['selectedCalendarItemDay']});
        this.emitChange();
        break;

      case EventConstants.CALENDAR_ITEM_SELECTED: {
        const {itemId, date} = action;
        if (
          isSelectedItemInteraction(this.interact) &&
          this.interact.selectedCalendarItemDay.id === itemId &&
          date.isSame(this.interact.selectedCalendarItemDay.date)
        ) {
          // Deselect if already selected.
          this.interact = reactUpdate(this.interact, {$unset: ['selectedCalendarItemDay']});
        } else {
          this.interact = reactUpdate(this.interact, {selectedCalendarItemDay: {$set: {id: itemId, date}}});
        }
        this.emitChange();
        break;
      }

      case EventConstants.CALENDAR_DATE_NAVIGATE:
        this.visibleDate = action.visibleDate;
        this.emitChange();
        break;

      case EventConstants.CALENDAR_VIEW_CHANGE:
        this.currentView = action.view;
        preferences.set(CalendarPreferences.PREFERRED_CALENDAR_VIEW, action.view);
        this.emitChange();
        break;

      case EventConstants.CALENDAR_TOGGLE_PROJECT_HIDDEN: {
        const {projectId} = action;
        const wasHidden = Array.from(this.hiddenProjectIds).includes(projectId);
        if (wasHidden) {
          this.hiddenProjectIds = this.hiddenProjectIds.filter((id) => id !== projectId);
        } else {
          this.hiddenProjectIds = this.hiddenProjectIds.concat([projectId]);
        }
        this.emitChange();
        break;
      }

      case EventConstants.CALENDAR_UPDATE_FILTERED_MEMBERS: {
        const {memberIds, shouldHide} = action;
        if (shouldHide) {
          this.hiddenMemberIds = _.union(this.hiddenMemberIds, memberIds);
        } else {
          this.hiddenMemberIds = _.difference(this.hiddenMemberIds, memberIds);
        }
        this.emitChange();
        break;
      }

      case EventConstants.CALENDAR_UPDATE_TODAY: {
        const newToday = this._makeToday();
        if (!newToday.isSame(this.today)) {
          this.today = newToday;
          this.emitChange();
        }
        break;
      }

      case EventConstants.CALENDAR_DAY_MOUSE_DOWN:
        if (isInactiveInteraction(this.interact)) {
          this.interact = reactUpdate(this.interact, {
            $merge: {
              mouseDownDate: action.date,
              mouseOverDate: action.date,
            },
          });
          this.emitChange();
        }
        break;

      case EventConstants.CALENDAR_DAY_MOUSE_OVER:
        if (isDraggingInteraction(this.interact) && action.date !== this.interact.mouseOverDate) {
          this.interact = reactUpdate(this.interact, {mouseOverDate: {$set: action.date}});
          this.emitChange();
        }
        break;

      case EventConstants.CALENDAR_DAY_MOUSE_UP: {
        const supportedView = [CalendarView.MONTH_VIEW, CalendarView.WEEK_VIEW].includes(this.currentView);
        if (supportedView && isDraggingInteraction(this.interact)) {
          this.interact = reactUpdate(this.interact, {
            $merge: {
              mouseUpCoords: {
                x: action.x,
                y: action.y,
              },
            },
          });
          this.emitChange();
        }
        break;
      }

      case EventConstants.CALENDAR_CLOSE_CREATE_POPOVER:
        this.interact = reactUpdate(this.interact, {
          $unset: ['mouseDownDate', 'mouseOverDate', 'mouseUpCoords'],
        });
        this.emitChange();
        break;

      default:
        break;
    }

    return true;
  }
}

const CalendarStoreInstance = new CalendarStore(AppDispatcher);
export default CalendarStoreInstance;
